export default class FiveCardHandParent {
  constructor(argsObj) {
    this.id = argsObj.id;
    this.text = argsObj.text;
    this.fullText = argsObj.fullText;
    this.inlineText = argsObj.inlineText;
    this.children = argsObj.children;
    this.collapsed = true;
    this.count = 0;
    this._probability = null;
    this.percentageOfSpecificHandsThatAreSelected = 0;
    this.isAHit = argsObj.isAHit;
  };

  get included() {
    for (let i = 0; i < this.children.length; i++) {
      if (this.children[i].included) {
        return true;
      }
    }

    return false;
  };
  get visible() {
    if (!this.included) {
      return false;
    }

    for (let i = 0; i < this.children.length; i++) {
      if (this.children[i].visible) {
        return true;
      }
    }

    return false;
  };
  get checked() {
    let anyCheckedChildren = false;
    let anyPartiallyCheckedChildren = false;
    let anyUncheckedChildren = false;
    let currChildCheckedValue;

    for (let i = 0; i < this.children.length; i++) {
      if (!this.children[i].visible) {
        continue;
      }

      currChildCheckedValue = this.children[i].checked;

      if (currChildCheckedValue === true) {
        anyCheckedChildren = true;
      } else if (currChildCheckedValue === 'partially') {
        anyPartiallyCheckedChildren = true;
      } else if (currChildCheckedValue === false) {
        anyUncheckedChildren = true;
      }
    }

    if (anyCheckedChildren && !anyPartiallyCheckedChildren && !anyUncheckedChildren) {
      return true;
    } else if (!anyCheckedChildren && !anyPartiallyCheckedChildren) {
      return false;
    } else {
      return 'partially';
    }
  };
  get probability() {
    if (this._probability > 100) {
      return 100;
    }

    return this._probability || 0;
  };
  get weight() {
    let currWeight;

    for (let i = 0; i < this.children.length; i++) {
      if (!this.children[i].visible) {
        continue;
      }

      if (!this.children[i].checked) {
        return undefined;
      }

      if (!this.children[i].weight) {
        return undefined;
      }

      // first visible, checked child
      if (!currWeight) {
        if (this.children[i].weight === 100) {
          return undefined;
        }

        currWeight = this.children[i].weight;
      }

      // after first visible, checked child
      else {
        if (this.children[i].weight !== currWeight) {
          return undefined;
        }
      }
    }

    return currWeight;
  };
  get hasWeight() {
    if (!this.visible || !this.checked) {
      return false;
    }

    for (let i = 0; i < this.children.length; i++) {
      if (this.children[i].hasWeight) {
        return true;
      }
    }

    return false;
  };
  get hasExplicitWeight() {
    if (
      this.visible &&
      this.checked &&
      this.weight &&
      (this.weight < 100)
    ) {
      return true;
    } else {
      return false;
    }
  };
  get matchingSpecificHands() {
    let matchingSpecificHands = [];

    this.children.forEach(function (child) {
      child.matchingSpecificHands.forEach(function (childMatchingSpecificHand) {
        if (!matchingSpecificHands.includes(childMatchingSpecificHand)) {
          matchingSpecificHands.push(childMatchingSpecificHand);
        }
      });
    });

    return matchingSpecificHands;
  };

  set checked(newCheckedValue) {
    if (!this.visible) {
      throw new Error('Trying to set the checked value of a fiveCardHand that isn\'t visible.');
    }

    if (newCheckedValue !== true && newCheckedValue !== false) {
      throw new Error('Invalid value for newCheckedValue.');
    }

    this.children.forEach(function (child) {
      if (child.visible) {
        child.checked = newCheckedValue;
      }
    });
  };
  set weight(newWeight) {
    this.children.forEach(function (child) {
      if (!child.visible) {
        return;
      }

      child.weight = newWeight;
    });
  }

  applyExactWeightToFiveCardHand(newWeight) {
    if (!this.visible) {
      throw new Error('Trying to call applyExactWeightToFiveCardHand on a fiveCardHand that isn\'t visible.');
    }

    this.children.forEach(function (child) {
      if (!child.visible) {
        return;
      }

      if (!child.children && child.checked) {
        child.weight = newWeight;
      }

      if (child.children && (child.checked === true || child.checked === 'partially')) {
        child.applyExactWeightToFiveCardHand(newWeight);
      }
    });
  };
  setProbabilityAccordingToMatchingSpecificHands(generalHands) {
    let numberOfPossibleSpecificHands = generalHands.numberOfPossibleSpecificHands;
    let numberOfMatchingSpecificHands = 0;
    let currSpecificHand;

    this.matchingSpecificHands.forEach(function (matchingSpecificHandString) {
      currSpecificHand =  generalHands.find(matchingSpecificHandString);
      numberOfMatchingSpecificHands += (currSpecificHand.maxPossibleWeight) / 100;
    });

    this._probability = (numberOfMatchingSpecificHands / numberOfPossibleSpecificHands) * 100;
  };
  getNumCombos(numberOfPossibleSpecificHands) {
    return ((this.probability / 100) * numberOfPossibleSpecificHands).roundToOneDecimalPlace();
  };
  setPercentageOfSpecificHandsThatAreSelected(generalHands) {
    if (this.matchingSpecificHands.length === 0) {
      if (this.checked) {
        return 100;
      } else {
        return 0;
      }
    }

    this.percentageOfSpecificHandsThatAreSelected = (
      this._getWeightedNumSpecificHandsThatAreSelected(generalHands) /
      this._getWeightedNumSpecificHandsThatArePossible(generalHands)
    ) * 100;
  };
  _getWeightedNumSpecificHandsThatAreSelected(generalHands) {
    let currSpecificHand;
    let weightedNumSpecificHandsThatAreSelected = 0;

    this.matchingSpecificHands.forEach(function (matchingSpecificHandString) {
      currSpecificHand = generalHands.find(matchingSpecificHandString);

      if (currSpecificHand.possible) {
        weightedNumSpecificHandsThatAreSelected += currSpecificHand.weight;
      }
    });

    return weightedNumSpecificHandsThatAreSelected;
  };
  _getWeightedNumSpecificHandsThatArePossible(generalHands) {
    let currSpecificHand;
    let weightedNumSpecificHandsThatAreSelected = 0;

    this.matchingSpecificHands.forEach(function (matchingSpecificHandString) {
      currSpecificHand = generalHands.find(matchingSpecificHandString);

      if (currSpecificHand.possible) {
        weightedNumSpecificHandsThatAreSelected += currSpecificHand.maxPossibleWeight;
      }
    });

    return weightedNumSpecificHandsThatAreSelected;
  };
};



// WEBPACK FOOTER //
// ./src/services/five-card-hands/five-card-hand-parent/FiveCardHandParent.js